import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ModuleType } from 'app/library/module/enums/moduleType.enum';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IUserQuery } from 'app/projects/user/src/lib/interfaces/user.query.interface';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ExtendedAttributeCategoryEnum } from '../enums/extended-attribute-category.enum';
import { IEditExtendedAttributeValue } from '../interfaces/edit-extended-attribute-value.interface';
import { IExtendedAttributesFilterAndTotalCount } from '../interfaces/extended-attribute-filter-values-pagination.query';
import { IExtendedAttributesAndTotalCount } from '../interfaces/extended-attribute-pagination.query';
import { IExtendedAttributeQuery } from '../interfaces/extended-attribute.query.interface';
import { EditExtendedAttributeValueCommand } from '../models/edit-extended-attribute-value.command.model';
import { EditExtendedAttributeCommand } from '../models/edit-extended-attribute.command.model';
import { ExtendedAttribute } from '../models/extended-attribute';
import { ExtendedAttributeFilter } from '../models/extended-attribute-filter.model';
import { ExtendedAttributeTranslationsCommand } from '../models/extended-attribute-translations.command.model';
import { ExtendedAttributeCommand } from '../models/extended-attribute.command.model';

@Injectable()
export class ExtendedAttributeApiService extends EntityService<IExtendedAttributeQuery, EditExtendedAttributeCommand> implements IEntityApiService<ExtendedAttribute> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IEnvironment) {
        super(http, _environment.apiUrl, 'admin/extended-attributes');
    }

    getAll(): Observable<ExtendedAttribute[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new ExtendedAttribute(query))));
    }

    findAllAdminAttributes(categories: number[], enabledForFilter: string): Observable<IExtendedAttributesAndTotalCount> {
        let params = new HttpParams().set('filter[category]', categories.join(','));

        if (enabledForFilter) {
            params = params.set('filter[enabled]', enabledForFilter);
        }

        return this.http.get<IExtendedAttributesAndTotalCount>(`${this._environment.apiUrl}/${this.entities}`, { params });
    }

    create(entity: ExtendedAttribute): Observable<ExtendedAttribute> {
        const data = new ExtendedAttributeCommand(entity);

        return this._create(data).pipe(map((query) => new ExtendedAttribute(query)));
    }

    read(id: string): Observable<ExtendedAttribute> {
        return this._read(id).pipe(map((query) => new ExtendedAttribute(query)));
    }

    update(entity: ExtendedAttribute): Observable<ExtendedAttribute> {
        const data = new EditExtendedAttributeCommand(entity);

        return this._update(entity.id, data).pipe(map((query) => new ExtendedAttribute(query)));
    }

    updateTranslations(entity: ExtendedAttribute): Observable<ExtendedAttribute> {
        const data = new ExtendedAttributeTranslationsCommand(entity);

        return this.http
            .put<IExtendedAttributeQuery>(`${this._environment.apiUrl}/${this.entities}/${entity.id}/translations`, data)
            .pipe(map((query) => new ExtendedAttribute(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    fetchAllExtendedAttributes(categories: number[], enabledForFilter?: ModuleType): Observable<IExtendedAttributesAndTotalCount> {
        let params = new HttpParams();

        if (categories?.length) {
            params = params.set('filter[category]', categories.join(','));
        }

        if (enabledForFilter) {
            params = params.set('filter[enabled]', enabledForFilter);
        }

        return this.http.get<IExtendedAttributesAndTotalCount>(`${this._environment.apiUrl}/extended-attributes`, { params });
    }

    updateExtendedAttributeValue(payload: IEditExtendedAttributeValue): Observable<User> {
        const data = new EditExtendedAttributeValueCommand(payload);

        return this.http.put<IUserQuery>(`${this._environment.apiUrl}/extended-attribute/user/value`, data).pipe(map((query) => new User(query)));
    }

    fetchFilterValues(): Promise<ExtendedAttributeFilter[]> {
        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/${this.entities}/filter-values`)
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    fetchParticipantFilterValues(): Promise<ExtendedAttributeFilter[]> {
        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/extended-attributes/filter-values`)
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    fetchApprovalRequestFilterValues(): Promise<ExtendedAttributeFilter[]> {
        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/extended-attributes/approval-request/filter-values`)
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    fetchCourseOfferingFilterValues(isPreview: boolean = false): Promise<ExtendedAttributeFilter[]> {
        let params = new HttpParams();

        if (isPreview) {
            params = params.set('filter[isPreview]', 'true');
        }

        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/extended-attributes/course-offering/filter-values`, { params })
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    fetchCourseExecutionFilterValues(enabledForFilter?: ModuleType): Promise<ExtendedAttributeFilter[]> {
        let params = new HttpParams();

        if (enabledForFilter) {
            params = params.set('filter[enabled]', enabledForFilter);
        }

        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/extended-attributes/course-execution/filter-values`, { params })
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    fetchCourseExecutionListingFilterValues(enabledForFilter: string, courseOfferingId: string): Promise<ExtendedAttributeFilter[]> {
        let params = new HttpParams()
            .set('filter[enabled]', enabledForFilter)
            .set('filter[offeringId]', courseOfferingId)
            .set('filter[view]', 'listing');

        return this.http
            .get<IExtendedAttributesFilterAndTotalCount>(`${this._environment.apiUrl}/extended-attributes/course-execution/filter-values`, { params })
            .pipe(map((res) => res.data.map((value) => new ExtendedAttributeFilter(value))))
            .toPromise();
    }

    validateAttributeName(name: string, category: ExtendedAttributeCategoryEnum): Promise<{ is_already_used: boolean }> {
        return this.http.post<{ is_already_used: boolean }>(`${this._environment.apiUrl}/${this.entities}/name/validate`, { name, category }).toPromise();
    }
}
